// =============================================================================
// BP-08-05: Vendor Portal Frontend - ImageUploader Component
// =============================================================================

import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, Image as ImageIcon, Loader2, GripVertical } from 'lucide-react';

interface ImageUploaderProps {
  existingImages?: string[];
  onUpload?: (files: File[]) => Promise<void>;
  onDelete?: (imageUrl: string) => Promise<void>;
  onReorder?: (urls: string[]) => void;
  maxFiles?: number;
  maxSizeMB?: number;
}

export function ImageUploader({
  existingImages = [],
  onUpload,
  onDelete,
  onReorder,
  maxFiles = 10,
  maxSizeMB = 10,
}: ImageUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!onUpload) return;

      // Check total count
      const totalCount = existingImages.length + acceptedFiles.length;
      if (totalCount > maxFiles) {
        setError(`Maximum ${maxFiles} images allowed`);
        return;
      }

      setUploading(true);
      setError(null);
      setUploadProgress(0);

      try {
        await onUpload(acceptedFiles);
        setUploadProgress(100);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Upload failed');
      } finally {
        setUploading(false);
        setUploadProgress(0);
      }
    },
    [onUpload, existingImages.length, maxFiles]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
      'image/gif': ['.gif'],
    },
    maxSize: maxSizeMB * 1024 * 1024,
    disabled: uploading || existingImages.length >= maxFiles,
    onDropRejected: (rejections) => {
      const rejection = rejections[0];
      if (rejection?.errors[0]?.code === 'file-too-large') {
        setError(`File too large. Maximum size is ${maxSizeMB}MB`);
      } else if (rejection?.errors[0]?.code === 'file-invalid-type') {
        setError('Invalid file type. Use JPG, PNG, WebP, or GIF');
      }
    },
  });

  const handleDelete = async (imageUrl: string) => {
    if (!onDelete) return;
    setDeleting(imageUrl);
    setError(null);
    try {
      await onDelete(imageUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(null);
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newImages = [...existingImages];
    const draggedItem = newImages[draggedIndex];
    newImages.splice(draggedIndex, 1);
    newImages.splice(index, 0, draggedItem);
    
    onReorder?.(newImages);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <div className="space-y-4">
      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Existing Images */}
      {existingImages.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {existingImages.map((url, index) => (
            <div
              key={url}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={`
                relative aspect-square rounded-xl overflow-hidden border-2 group
                ${draggedIndex === index ? 'border-terracotta opacity-50' : 'border-gray-200'}
                ${index === 0 ? 'ring-2 ring-terracotta ring-offset-2' : ''}
              `}
            >
              <img
                src={url}
                alt={`Product image ${index + 1}`}
                className="w-full h-full object-cover"
              />

              {/* Primary Badge */}
              {index === 0 && (
                <span className="absolute top-2 left-2 px-2 py-0.5 bg-terracotta text-white text-xs font-semibold rounded">
                  Primary
                </span>
              )}

              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <button
                  className="p-2 bg-white/90 rounded-lg hover:bg-white transition-colors cursor-move"
                  aria-label="Drag to reorder"
                >
                  <GripVertical className="w-4 h-4 text-gray-700" />
                </button>
                <button
                  onClick={() => handleDelete(url)}
                  disabled={deleting === url}
                  className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                  aria-label="Delete image"
                >
                  {deleting === url ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <X className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Area */}
      {existingImages.length < maxFiles && (
        <div
          {...getRootProps()}
          className={`
            border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors
            ${isDragActive ? 'border-terracotta bg-terracotta/5' : 'border-gray-300 hover:border-terracotta'}
            ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center gap-3">
            {uploading ? (
              <>
                <Loader2 className="w-10 h-10 text-terracotta animate-spin" />
                <p className="text-sm text-warm-gray">Uploading... {uploadProgress}%</p>
              </>
            ) : isDragActive ? (
              <>
                <Upload className="w-10 h-10 text-terracotta" />
                <p className="text-sm font-medium text-terracotta">Drop images here</p>
              </>
            ) : (
              <>
                <ImageIcon className="w-10 h-10 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-navy">
                    Drag and drop images here, or click to browse
                  </p>
                  <p className="text-xs text-warm-gray mt-1">
                    JPG, PNG, WebP, GIF • Max {maxSizeMB}MB each • Up to {maxFiles} images
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Image Count */}
      <p className="text-sm text-warm-gray text-center">
        {existingImages.length} of {maxFiles} images uploaded
      </p>
    </div>
  );
}

export default ImageUploader;
